import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useRazorpay } from "react-razorpay";
import "./Checkout.css";

const Checkout = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { Razorpay } = useRazorpay();
  const [cartItems, setCartItems] = useState([]);
  const [product, setProduct] = useState(null);
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    address: "",
    phone: "",
  });
  const [user, setUser] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("ONLINE");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Load user from localStorage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      setUser(storedUser);
      setUserDetails({
        name: storedUser.name,
        email: storedUser.email,
        address: storedUser.address || "",
        phone: storedUser.phone || "",
      });
    }
  }, []);

  // Fetch product or cart
  useEffect(() => {
    if (productId === "cart") {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      // Ensure cart items have integer prices
      const parsedCart = cart.map(item => ({
        ...item,
        price: parseInt(item.price),
      }));
      setCartItems(parsedCart);
      setLoading(false);
    } else {
      const fetchProduct = async () => {
        try {
          const response = await fetch(`https://kothari-international-server.onrender.com/api/products/${productId}`);
          if (!response.ok) {
            throw new Error(`Product not found (status: ${response.status})`);
          }
          const foundProduct = await response.json();
          setProduct({
            ...foundProduct,
            price: parseInt(foundProduct.price),
          });
        } catch (error) {
          console.error("Error fetching product:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchProduct();
    }
  }, [productId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handlePayment = async () => {
    if (!user) {
      setError("Please log in to proceed with checkout.");
      return;
    }
    if (!userDetails.name || !userDetails.email || !userDetails.address || !userDetails.phone) {
      alert("Please fill all shipping details.");
      return;
    }

    const totalAmount = productId === "cart"
      ? cartItems.reduce((total, item) => total + item.price * item.quantity, 0)
      : product.price;

    try {
      // Create order
      const orderResponse = await fetch("https://kothari-international-server.onrender.com/api/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          cartItems: productId === "cart" ? cartItems : [{ id: product.id, quantity: 1, price: product.price }],
          totalAmount,
          paymentMethod,
        }),
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.error || "Failed to create order");
      }

      const { orderId, razorpayOrderId, amount } = await orderResponse.json();

      // Handle payment (COD advance or online)
      if (amount > 0) {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount,
          currency: "INR",
          name: "Kothari Luggage Mall",
          description: paymentMethod === "COD" ? "COD Advance Payment" : "Order Payment",
          order_id: razorpayOrderId,
          handler: async (response) => {
            try {
              const verifyResponse = await fetch("https://kothari-international-server.onrender.com/api/confirm-payment", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  orderId,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });

              const verifyResult = await verifyResponse.json();
              if (verifyResult.status === "success") {
                if (productId === "cart") {
                  localStorage.removeItem("cart");
                  window.dispatchEvent(new Event("storageUpdate"));
                }
                navigate(`/order-confirmation/${orderId}`);
              } else {
                alert("Payment verification failed.");
              }
            } catch (err) {
              alert("Error verifying payment: " + err.message);
            }
          },
          prefill: {
            name: userDetails.name,
            email: userDetails.email,
            contact: userDetails.phone,
          },
          theme: { color: "#28a745" },
        };

        const rzpay = new Razorpay(options);
        rzpay.open();
      } else {
        // No payment needed
        await fetch("https://kothari-international-server.onrender.com/api/confirm-payment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderId }),
        });
        if (productId === "cart") {
          localStorage.removeItem("cart");
          window.dispatchEvent(new Event("storageUpdate"));
        }
        navigate(`/order-confirmation/${orderId}`);
      }
    } catch (err) {
      alert("Error processing order: " + err.message);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (productId === "cart" && cartItems.length === 0) return <p>Cart is empty</p>;
  if (productId !== "cart" && !product) return <p>Product not found</p>;

  const totalAmount = productId === "cart"
    ? cartItems.reduce((total, item) => total + item.price * item.quantity, 0)
    : product.price;

  return (
    <div className="checkout">
      <h1>Checkout</h1>
      {!user && (
        <div className="login-prompt">
          <p>Please log in to proceed with checkout.</p>
          <button onClick={() => navigate("/login")}>Log In / Register</button>
        </div>
      )}
      <div className="checkout-container">
        <div className="product-summary">
          <h2>{productId === "cart" ? "Cart Items" : "Product Details"}</h2>
          {productId === "cart" ? (
            cartItems.map((item) => (
              <div key={item.id} className="product-item">
                <img src={item.image} alt={item.name} />
                <div>
                  <h3>{item.name}</h3>
                  <p>
                    ₹{item.price} x {item.quantity}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="product-item">
              <img src={product.image} alt={product.name} />
              <div>
                <h3>{product.name}</h3>
                <p>₹{product.price}</p>
              </div>
            </div>
          )}
          <p>Total: ₹{totalAmount}</p>
        </div>
        <div className="user-details">
          <h2>Shipping Information</h2>
          <form>
            <input
              type="text"
              name="name"
              value={userDetails.name}
              onChange={handleInputChange}
              placeholder="Full Name"
              required
            />
            <input
              type="email"
              name="email"
              value={userDetails.email}
              onChange={handleInputChange}
              placeholder="Email"
              required
            />
            <input
              type="text"
              name="address"
              value={userDetails.address}
              onChange={handleInputChange}
              placeholder="Address"
              required
            />
            <input
              type="text"
              name="phone"
              value={userDetails.phone}
              onChange={handleInputChange}
              placeholder="Phone Number"
              required
            />
          </form>
          <h2>Payment Method</h2>
          <div className="payment-methods">
            <label>
              <input
                type="radio"
                value="ONLINE"
                checked={paymentMethod === "ONLINE"}
                onChange={() => setPaymentMethod("ONLINE")}
              />
              Online Payment (Pay ₹{totalAmount})
            </label>
            <label>
              <input
                type="radio"
                value="COD"
                checked={paymentMethod === "COD"}
                onChange={() => setPaymentMethod("COD")}
              />
              Cash on Delivery (Pay ₹60 advance to confirm order and cover processing costs)
            </label>
          </div>
        </div>
      </div>
      <button
        className="pay-btn"
        onClick={handlePayment}
        disabled={!user || !userDetails.name || !userDetails.email || !userDetails.address || !userDetails.phone}
      >
        {paymentMethod === "COD" ? "Pay ₹60 Advance" : "Pay Now"}
      </button>
    </div>
  );
};

export default Checkout;